import type { ChatUi } from '@voll-intelligence/genui-contract';
import type { GenUiRenderProps, GenUiEntry } from '../../shared/ui/registry.js';
import { useGenUiI18n } from '../../shared/ui/i18n.js';
import { useGenUiConfig } from '../../shared/ui/config.js';
import type { CheckoutOutcome } from './core/use_one_click_checkout.js';
import { CheckoutCard } from './ui/checkout_card.js';

type ProductCheckoutProps = GenUiRenderProps<Extract<ChatUi, { kind: 'product_checkout' }>>;

/** Host sem o `oneClick`: nenhum desfecho a dizer. */
function useNoOutcome(): CheckoutOutcome | undefined {
  return undefined;
}

/** Wrapper component (not a plain function) so `t()` can be a hook — `render`
 *  itself isn't rendered as a component, so hooks can't live directly in it. */
function ProductCheckoutBlock({
  ui,
  note,
  readOnly,
  replay,
  busy,
  dispatch,
}: Readonly<ProductCheckoutProps>) {
  const { t } = useGenUiI18n();
  const oneClick = useGenUiConfig().oneClick;
  // O port é do host e não troca durante a vida do card: a escolha do hook é
  // estável entre renders.
  // eslint-disable-next-line react/hooks -- the host injects a stable hook through the port; moving the port to a component or store is #6816
  const useOutcome = oneClick?.useOutcome ?? useNoOutcome;
  // eslint-disable-next-line react/hooks -- the host injects a stable hook through the port; moving the port to a component or store is #6816
  const outcome = useOutcome(ui.checkout.challengeId);
  return (
    <CheckoutCard
      checkout={ui.checkout}
      note={note}
      readOnly={readOnly}
      replay={replay}
      busy={busy}
      {...(outcome ? { outcome } : {})}
      {...(oneClick ? { onArrive: oneClick.arrive } : {})}
      onSubmit={(action) => {
        dispatch(action);
      }}
      onChooseAnother={
        readOnly
          ? undefined
          : () => {
              dispatch({
                type: 'interaction',
                interactionId: ui.checkout.challengeId,
                text: t('productCheckout.chooseAnotherReply'),
                data: { action: 'other_options' },
              });
            }
      }
    />
  );
}

/** Entrada de catálogo do `product_checkout` (slot chat). The card owns the
 *  drawer; submit emits `product-checkout-submit`, which the shell maps to
 *  `send(...)`. O CTA "Ver outras opções" emite `interaction` com a selection `other_options`
 *  — interceptada pelo map-guard do domínio no agents, nunca pelo LLM. */
export const productCheckoutEntry: GenUiEntry<Extract<ChatUi, { kind: 'product_checkout' }>> = {
  kind: 'product_checkout',
  slot: 'chat',
  policy: {
    // #5264: seguir para outro produto (o hotel depois do voo) não congela este
    // checkout; outra busca ou outro checkout do mesmo produto, sim.
    productOf: (ui) => ui.checkout.items[0]?.productType,
  },
  render: (props) => <ProductCheckoutBlock {...props} />,
};
